import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { jsPDF } from 'jspdf';
import {
  FileText, CheckCircle2, AlertTriangle, Download, ShieldCheck, ArrowLeft, Lock, Loader2, ClipboardList, MessageCircle
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Checkbox } from '../ui/checkbox';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/tabs';
import { toast } from 'sonner';
import SEO from '../SEO';
import StudentCenter from './StudentCenter';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS = {
  pending: { label: 'Aguardando aceite', color: 'bg-amber-600' },
  accepted: { label: 'Matrícula concluída', color: 'bg-green-600' },
  blocked: { label: 'Validação administrativa', color: 'bg-red-600' },
  canceled: { label: 'Cancelado', color: 'bg-zinc-600' },
};

// ---------------- Login inline ----------------
const InlineLogin = () => {
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (!res.success) toast.error(res.error || 'Falha no login');
  };

  return (
    <div className="min-h-screen bg-black flex items-center justify-center px-6">
      <form onSubmit={submit} className="w-full max-w-md bg-zinc-900 border border-zinc-800 rounded-2xl p-8" data-testid="portal-login">
        <div className="flex items-center gap-2 text-red-500 mb-6"><Lock size={20} /><span className="font-semibold">Portal do Aluno</span></div>
        <h1 className="text-2xl font-bold text-white mb-2">Entre na sua conta</h1>
        <p className="text-gray-400 text-sm mb-6">Acesse com o mesmo e-mail usado na compra para ver suas contratações.</p>
        <div className="bg-amber-950/40 border border-amber-600/50 rounded-lg p-3 mb-6 text-xs" data-testid="portal-login-version-notice">
          <p className="text-amber-200 font-semibold">Sistema de matrículas on-line na versão 1.0</p>
          <p className="text-amber-100/80 mt-0.5">Em caso de alguma falha, entrar em contato direto com nossos atendentes. O ambiente de matrículas está em ajustes finais.</p>
          <a href="https://wa.me/5561995792667?text=Ol%C3%A1%2C%20preciso%20de%20ajuda%20com%20minha%20matr%C3%ADcula%20no%20Portal%20do%20Aluno."
             target="_blank" rel="noopener noreferrer"
             className="inline-flex items-center gap-2 mt-2.5 bg-green-600 hover:bg-green-700 text-white text-xs font-medium px-3 py-1.5 rounded-md transition-colors"
             data-testid="portal-login-version-whatsapp">
            <MessageCircle size={14} />Falar com um atendente
          </a>
        </div>
        <div className="space-y-4">
          <div><Label className="text-gray-300">E-mail</Label><Input type="email" value={email} onChange={e => setEmail(e.target.value)} className="bg-zinc-800 border-zinc-700 text-white" data-testid="portal-login-email" required /></div>
          <div><Label className="text-gray-300">Senha</Label><Input type="password" value={password} onChange={e => setPassword(e.target.value)} className="bg-zinc-800 border-zinc-700 text-white" data-testid="portal-login-password" required /></div>
          <Button type="submit" disabled={loading} className="w-full bg-red-600 hover:bg-red-700" data-testid="portal-login-submit">
            {loading ? <Loader2 className="animate-spin" size={18} /> : 'Entrar'}
          </Button>
        </div>
      </form>
    </div>
  );
};

// ---------------- Renderização dos documentos ----------------
const Row = ({ label, value }) => (
  <div className="flex flex-col sm:flex-row sm:gap-2 py-1 border-b border-zinc-800/50">
    <span className="text-gray-400 text-sm min-w-[220px]">{label}</span>
    <span className="text-white text-sm font-medium">{value || '—'}</span>
  </div>
);

const DocSection = ({ n, title, children }) => (
  <div className="mb-5">
    <h4 className="text-red-400 font-bold text-sm uppercase tracking-wide mb-2">{n}. {title}</h4>
    {children}
  </div>
);

const AnexoIView = ({ d }) => (
  <div data-testid="doc-anexo-i">
    <DocSection n="1" title="Identificação do Contratante">
      <Row label="Nome Completo" value={d.student.name} />
      <Row label="CPF" value={d.student.cpf} />
      <Row label="E-mail" value={d.student.email} />
      <Row label="Telefone" value={d.student.phone} />
      <Row label="Cidade/UF" value={d.student.city} />
    </DocSection>
    <DocSection n="2" title="Identificação do Programa Educacional">
      <Row label="Nome do Programa" value={d.program.name} />
      <Row label="Código do Programa" value={d.program.program_code} />
      <Row label="Categoria" value={d.program.category} />
      <Row label="Área de Conhecimento" value={d.program.knowledge_area} />
      <Row label="Modalidade" value={d.program.modality} />
      <Row label="Código da Turma" value={d.program.class_code} />
    </DocSection>
    <DocSection n="3" title="Características do Programa">
      <Row label="Carga Horária Total" value={d.program.workload} />
      <Row label="Prazo de acesso ao conteúdo" value={d.program.access_period} />
      <Row label="Idioma" value={d.program.language} />
      <Row label="Plataforma(s)" value={d.program.platform} />
      <Row label="Professor(es) responsável(is)" value={d.program.professor} />
    </DocSection>
    <DocSection n="4" title="Objetivo do Programa"><p className="text-gray-300 text-sm">{d.program.objective}</p></DocSection>
    {(d.program.syllabus || []).length > 0 && (
      <DocSection n="5" title="Ementa / Conteúdo Programático">
        <ul className="list-disc list-inside text-gray-300 text-sm space-y-1">
          {d.program.syllabus.map((s, i) => <li key={i}>{s}</li>)}
        </ul>
      </DocSection>
    )}
    <DocSection n="6" title="Benefícios Incluídos">
      <div className="flex flex-wrap gap-2">{(d.program.benefits || []).map(b => <Badge key={b} className="bg-zinc-800 text-gray-200">{b}</Badge>)}</div>
    </DocSection>
    <DocSection n="8" title="Certificação"><p className="text-gray-300 text-sm">{d.program.certification}</p></DocSection>
    <DocSection n="10" title="Declaração">
      <p className="text-gray-400 text-xs leading-relaxed">O CONTRATANTE declara que: I – recebeu todas as informações referentes ao Programa Educacional contratado; II – conhece sua modalidade de oferta; III – recebeu informações sobre carga horária, período de acesso, conteúdo e certificação; IV – teve acesso ao Contrato Geral de Prestação de Serviços Educacionais; V – reconhece que este documento integra o Contrato Geral para todos os efeitos legais; VI – declara estar ciente de que as condições financeiras constam exclusivamente do Plano Financeiro Individual.</p>
    </DocSection>
  </div>
);

const PlanoView = ({ d }) => (
  <div data-testid="doc-plano-financeiro">
    <DocSection n="1" title="Identificação do Contratante">
      <Row label="Nome Completo" value={d.student.name} />
      <Row label="CPF" value={d.student.cpf} />
      <Row label="E-mail" value={d.student.email} />
    </DocSection>
    <DocSection n="2" title="Programa Educacional Contratado">
      <Row label="Programa" value={d.program.name} />
      <Row label="Código do Programa" value={d.program.program_code} />
      <Row label="Código da Turma" value={d.program.class_code} />
      <Row label="Modalidade" value={d.program.modality} />
    </DocSection>
    <DocSection n="4" title="Resumo Financeiro">
      <Row label="Valor Público do Programa" value={d.financial.public_price_fmt} />
      <Row label="Desconto Concedido" value={d.financial.discount_fmt} />
      <Row label="Percentual de Desconto" value={`${d.financial.discount_pct}%`} />
      <Row label="Valor Total Contratado" value={d.financial.total_contracted_fmt} />
    </DocSection>
    <DocSection n="5" title="Forma de Pagamento">
      <Row label="Forma de Pagamento" value={d.financial.payment_type} />
      <Row label="Parcelamento" value={`${d.financial.installments}x`} />
      <Row label="Cupom" value={d.financial.coupon} />
    </DocSection>
    <DocSection n="8" title="Benefícios Contratados">
      <div className="flex flex-wrap gap-2">{(d.benefits || []).map(b => <Badge key={b} className="bg-zinc-800 text-gray-200">{b}</Badge>)}</div>
    </DocSection>
    <DocSection n="11" title="Declaração">
      <p className="text-gray-400 text-xs leading-relaxed">O CONTRATANTE declara que: I – recebeu todas as informações referentes ao Programa; II – conhece a modalidade escolhida; III – recebeu esclarecimentos acerca das condições financeiras; IV – teve acesso ao Contrato Geral; V – concorda com os benefícios previstos; VI – reconhece que este Plano Financeiro integra o Contrato Geral para todos os efeitos legais.</p>
    </DocSection>
  </div>
);

const TermoView = ({ d, acceptance }) => (
  <div data-testid="doc-termo">
    <DocSection n="1" title="Objetivo">
      <p className="text-gray-400 text-xs leading-relaxed">O presente Termo formaliza a adesão do CONTRATANTE ao Programa Educacional contratado, registra seu aceite eletrônico e confirma a ciência acerca da modalidade escolhida, integrando o Contrato Geral, o Anexo I, o Plano Financeiro Individual e os demais documentos institucionais.</p>
    </DocSection>
    <DocSection n="2" title="Identificação">
      <Row label="Contratante" value={d.student.name} />
      <Row label="CPF" value={d.student.cpf} />
      <Row label="Programa" value={d.program.name} />
      <Row label="Código do Programa" value={d.program.program_code} />
      <Row label="Código da Turma" value={d.program.class_code} />
      <Row label="Modalidade" value={d.program.modality} />
    </DocSection>
    <DocSection n="4" title="Declarações">
      <p className="text-gray-400 text-xs leading-relaxed">O CONTRATANTE declara que: I – leu o Contrato Geral; II – recebeu o Plano Financeiro Individual; III – conhece os benefícios da modalidade contratada; IV – compreende que modalidades distintas possuem características diferentes; V – está ciente de que alteração de modalidade depende de aprovação da CONTRATADA; VI – recebeu acesso aos documentos institucionais; VII – manifesta seu aceite eletrônico às condições da contratação.</p>
    </DocSection>
    <DocSection n="6" title="Declaração Final">
      <p className="text-gray-400 text-xs leading-relaxed">O CONTRATANTE declara que leu, compreendeu e aceitou integralmente os documentos que compõem a contratação, reconhecendo que sua adesão foi realizada de forma livre, consciente e por meio eletrônico.</p>
    </DocSection>
    {acceptance && (
      <DocSection n="7" title="Formalização Eletrônica (registro do aceite)">
        <Row label="Data/Hora" value={new Date(acceptance.accepted_at).toLocaleString('pt-BR')} />
        <Row label="IP" value={acceptance.ip} />
        <Row label="Navegador/Dispositivo" value={acceptance.user_agent} />
        <Row label="E-mail" value={acceptance.email} />
        <Row label="ID da Assinatura" value={acceptance.signature_id} />
        <Row label="Hash (SHA-256)" value={acceptance.hash} />
      </DocSection>
    )}
  </div>
);

// PDF simples e fiel ao conteúdo
const generatePDF = (contract) => {
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  const W = 210; let y = 0;
  const docs = contract.documents;
  const addHeader = (title, code) => {
    doc.setFillColor(24, 24, 27); doc.rect(0, 0, W, 22, 'F');
    doc.setTextColor(255); doc.setFont('helvetica', 'bold'); doc.setFontSize(13);
    doc.text('AJ | BIM ACADEMY', 14, 10);
    doc.setFontSize(10); doc.setFont('helvetica', 'normal');
    doc.text(`${title}  (${code})`, 14, 17);
    y = 30; doc.setTextColor(20);
  };
  const line = (label, value) => {
    if (y > 275) { doc.addPage(); y = 20; }
    doc.setFont('helvetica', 'bold'); doc.setFontSize(9); doc.text(`${label}:`, 14, y);
    doc.setFont('helvetica', 'normal');
    const txt = doc.splitTextToSize(String(value ?? '—'), 120);
    doc.text(txt, 75, y); y += Math.max(6, txt.length * 5);
  };
  const heading = (t) => { if (y > 270) { doc.addPage(); y = 20; } doc.setFont('helvetica', 'bold'); doc.setFontSize(10); doc.setTextColor(200, 30, 30); doc.text(t, 14, y); doc.setTextColor(20); y += 7; };

  // Anexo I
  const a = docs.anexo_i;
  addHeader(a.title, a.code);
  heading('1. Identificação do Contratante');
  line('Nome', a.student.name); line('CPF', a.student.cpf); line('E-mail', a.student.email); line('Telefone', a.student.phone);
  heading('2. Programa Educacional');
  line('Programa', a.program.name); line('Código', a.program.program_code); line('Modalidade', a.program.modality); line('Turma', a.program.class_code);
  line('Carga Horária', a.program.workload); line('Professor', a.program.professor); line('Certificação', a.program.certification);
  line('Benefícios', (a.program.benefits || []).join(', '));

  // Plano Financeiro
  const p = docs.plano_financeiro;
  doc.addPage(); addHeader(p.title, p.code);
  heading('Identificação'); line('Nome', p.student.name); line('CPF', p.student.cpf);
  heading('Programa'); line('Programa', p.program.name); line('Código', p.program.program_code); line('Turma', p.program.class_code);
  heading('Resumo Financeiro');
  line('Valor Público', p.financial.public_price_fmt); line('Desconto', `${p.financial.discount_fmt} (${p.financial.discount_pct}%)`);
  line('Valor Total Contratado', p.financial.total_contracted_fmt); line('Forma de Pagamento', p.financial.payment_type);
  line('Parcelamento', `${p.financial.installments}x`);

  // Termo
  const t = docs.termo;
  doc.addPage(); addHeader(t.title, t.code);
  heading('Identificação'); line('Contratante', t.student.name); line('CPF', t.student.cpf);
  line('Programa', t.program.name); line('Modalidade', t.program.modality);
  if (contract.acceptance) {
    heading('Formalização Eletrônica');
    line('Data/Hora', new Date(contract.acceptance.accepted_at).toLocaleString('pt-BR'));
    line('IP', contract.acceptance.ip); line('E-mail', contract.acceptance.email);
    line('ID Assinatura', contract.acceptance.signature_id); line('Hash', contract.acceptance.hash);
  }
  if (contract.matricula) { heading('Matrícula'); line('Número da Matrícula', contract.matricula); }

  doc.save(`contrato-${contract.matricula || contract.id}.pdf`);
};

// ---------------- Visualização de um contrato ----------------
const ContractView = ({ contractId, onBack }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [agree, setAgree] = useState(false);
  const [cpf, setCpf] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/contracts/${contractId}`, { withCredentials: true });
      setData(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao carregar contrato');
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [contractId]);

  const accept = async () => {
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/contracts/${contractId}/accept`,
        { accepted: true, cpf: cpf || undefined, device: navigator.platform }, { withCredentials: true });
      toast.success(`Matrícula concluída: ${res.data.matricula}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Não foi possível concluir');
    } finally { setSubmitting(false); }
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-red-500" size={32} /></div>;
  if (!data) return null;

  const accepted = data.status === 'accepted';
  const blocked = data.status === 'blocked' || (data.missing_fields && data.missing_fields.length > 0);

  return (
    <div className="max-w-4xl mx-auto" data-testid="contract-view">
      <button onClick={onBack} className="flex items-center gap-2 text-gray-400 hover:text-white mb-6"><ArrowLeft size={18} />Voltar</button>

      {accepted && (
        <div className="bg-green-900/20 border border-green-500/40 rounded-xl p-5 mb-6 flex items-start gap-3" data-testid="contract-accepted-banner">
          <CheckCircle2 className="text-green-400 mt-0.5" />
          <div>
            <p className="text-white font-semibold">Matrícula concluída com sucesso!</p>
            <p className="text-gray-300 text-sm">Nº da Matrícula: <strong>{data.matricula}</strong> · Assinatura: {data.acceptance?.signature_id}</p>
          </div>
        </div>
      )}

      {blocked && !accepted && (
        <div className="bg-red-900/20 border border-red-500/40 rounded-xl p-5 mb-6" data-testid="contract-blocked-banner">
          <div className="flex items-center gap-2 text-red-400 font-semibold mb-2"><AlertTriangle size={18} />Contratação pendente de validação administrativa</div>
          <p className="text-gray-300 text-sm mb-2">{data.message}</p>
          <ul className="list-disc list-inside text-gray-400 text-sm">
            {data.missing_fields.map((m, i) => <li key={i}>{m}</li>)}
          </ul>
        </div>
      )}

      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-bold text-white flex items-center gap-2"><FileText className="text-red-500" size={22} />Documentos da Contratação</h2>
        <Button variant="outline" onClick={() => generatePDF(data)} data-testid="contract-download-pdf"><Download size={16} className="mr-2" />Baixar PDF</Button>
      </div>

      <Tabs defaultValue="anexo" className="w-full">
        <TabsList className="bg-zinc-900">
          <TabsTrigger value="anexo" data-testid="tab-anexo">Anexo I</TabsTrigger>
          <TabsTrigger value="plano" data-testid="tab-plano">Plano Financeiro</TabsTrigger>
          <TabsTrigger value="termo" data-testid="tab-termo">Termo de Adesão</TabsTrigger>
        </TabsList>
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6 mt-3">
          <TabsContent value="anexo"><AnexoIView d={data.documents.anexo_i} /></TabsContent>
          <TabsContent value="plano"><PlanoView d={data.documents.plano_financeiro} /></TabsContent>
          <TabsContent value="termo"><TermoView d={data.documents.termo} acceptance={accepted ? data.acceptance : null} /></TabsContent>
        </div>
      </Tabs>

      {!accepted && !blocked && (
        <div className="bg-zinc-900 border border-red-500/30 rounded-xl p-6 mt-6" data-testid="acceptance-box">
          <div className="flex items-center gap-2 text-red-400 font-semibold mb-4"><ShieldCheck size={18} />Aceite Eletrônico</div>
          {data.needs_cpf && (
            <div className="mb-4 max-w-xs">
              <Label className="text-gray-300">Informe seu CPF</Label>
              <Input value={cpf} onChange={e => setCpf(e.target.value)} placeholder="000.000.000-00" className="bg-zinc-800 border-zinc-700 text-white" data-testid="acceptance-cpf" />
            </div>
          )}
          <label className="flex items-start gap-3 cursor-pointer mb-4">
            <Checkbox checked={agree} onCheckedChange={setAgree} data-testid="acceptance-checkbox" />
            <span className="text-gray-300 text-sm">Li e aceito integralmente o Contrato Geral, o Anexo I, o Plano Financeiro Individual e o Termo de Adesão. Manifesto meu aceite eletrônico de forma livre e consciente.</span>
          </label>
          <Button disabled={!agree || submitting || (data.needs_cpf && !cpf)} onClick={accept} className="bg-red-600 hover:bg-red-700" data-testid="acceptance-submit">
            {submitting ? <Loader2 className="animate-spin" size={18} /> : 'Aceitar e concluir matrícula'}
          </Button>
        </div>
      )}
    </div>
  );
};

// ---------------- Página principal ----------------
const PortalAlunoPage = () => {
  const { isAuthenticated, loading: authLoading, user } = useAuth();
  const [contracts, setContracts] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('contratos');

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/contracts/my`, { withCredentials: true });
      setContracts(res.data);
    } catch (e) {
      // silencioso
    } finally { setLoading(false); }
  };

  useEffect(() => { if (isAuthenticated) load(); }, [isAuthenticated]);

  if (authLoading) return <div className="min-h-screen bg-black flex items-center justify-center"><Loader2 className="animate-spin text-red-500" size={32} /></div>;
  if (!isAuthenticated) return <InlineLogin />;

  const TABS = [
    { key: 'contratos', label: 'Meus Contratos', icon: FileText },
    { key: 'documentos', label: 'Documentos', icon: ShieldCheck },
    { key: 'notas', label: 'Notas & Avaliações', icon: ClipboardList },
  ];

  return (
    <div className="min-h-screen bg-black py-12 px-6" data-testid="portal-aluno-page">
      <SEO title="Central do Aluno | AJ | BIM ACADEMY" description="Acesse seus contratos, documentos, notas e certificados." />
      <div className="max-w-4xl mx-auto">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-white flex items-center gap-3"><ClipboardList className="text-red-500" size={28} />Central do Aluno</h1>
          <p className="text-gray-400 mt-1">Olá, {user?.name?.split(' ')[0]}. Seus contratos, documentos, notas e certificados em um só lugar.</p>
        </div>

        <div className="bg-amber-950/40 border border-amber-600/50 rounded-xl p-4 mb-6 flex items-start gap-3" data-testid="portal-version-notice">
          <AlertTriangle className="text-amber-400 mt-0.5 shrink-0" size={20} />
          <div className="text-sm flex-1">
            <p className="text-amber-200 font-semibold">Sistema de matrículas on-line na versão 1.0</p>
            <p className="text-amber-100/80 mt-0.5">Em caso de alguma falha, entrar em contato direto com nossos atendentes. O ambiente de matrículas está em ajustes finais.</p>
            <a href="https://wa.me/5561995792667?text=Ol%C3%A1%2C%20preciso%20de%20ajuda%20com%20minha%20matr%C3%ADcula%20no%20Portal%20do%20Aluno."
               target="_blank" rel="noopener noreferrer"
               className="inline-flex items-center gap-2 mt-3 bg-green-600 hover:bg-green-700 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors"
               data-testid="portal-version-whatsapp">
              <MessageCircle size={16} />Falar com um atendente
            </a>
          </div>
        </div>

        {!selected && (
          <div className="flex gap-2 mb-6 border-b border-zinc-800" data-testid="portal-tabs">
            {TABS.map(t => {
              const Icon = t.icon;
              return (
                <button key={t.key} onClick={() => setTab(t.key)}
                  className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === t.key ? 'border-red-500 text-white' : 'border-transparent text-gray-400 hover:text-gray-200'}`}
                  data-testid={`portal-tab-${t.key}`}>
                  <Icon size={16} />{t.label}
                </button>
              );
            })}
          </div>
        )}

        {selected ? (
          <ContractView contractId={selected} onBack={() => { setSelected(null); load(); }} />
        ) : tab === 'documentos' ? (
          <StudentCenter tab="documentos" />
        ) : tab === 'notas' ? (
          <StudentCenter tab="notas" />
        ) : loading ? (
          <div className="flex justify-center py-20"><Loader2 className="animate-spin text-red-500" size={32} /></div>
        ) : contracts.length === 0 ? (
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-10 text-center text-gray-400" data-testid="portal-empty">
            Você ainda não possui contratações. Após uma compra aprovada, ela aparecerá aqui automaticamente.
          </div>
        ) : (
          <div className="space-y-3" data-testid="contracts-list">
            {contracts.map(c => {
              const st = STATUS[c.status] || STATUS.pending;
              return (
                <button key={c.id} onClick={() => setSelected(c.id)}
                  className="w-full text-left bg-zinc-900 border border-zinc-800 rounded-xl p-5 hover:border-red-500/50 transition-all flex items-center justify-between"
                  data-testid={`contract-item-${c.id}`}>
                  <div>
                    <div className="text-white font-semibold">{c.program_name}</div>
                    <div className="text-gray-400 text-sm">{c.program_code} · {c.modality}{c.matricula ? ` · Matrícula ${c.matricula}` : ''}</div>
                  </div>
                  <Badge className={`${st.color} text-white`}>{st.label}</Badge>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default PortalAlunoPage;
